import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getMessageThread, replyToMessageThread } from '../api';

const POLL_INTERVAL = 30_000;

function formatTime(isoString) {
  const d = new Date(isoString);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function MessageThreadPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const fetchThread = useCallback(async () => {
    try {
      const res = await getMessageThread(id);
      setThread(res.data);
      setNotFound(false);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchThread();
    const intervalId = setInterval(fetchThread, POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchThread]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [thread]);

  const handleSend = async () => {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const res = await replyToMessageThread(id, body);
      setThread((prev) => prev ? { ...prev, messages: [...prev.messages, res.data] } : prev);
      setInput('');
    } catch {
      // leave input intact so the user can retry
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (notFound) {
    return (
      <div style={{ maxWidth: 680, margin: '0 auto', padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
        <p>This message thread couldn't be found.</p>
        <button
          onClick={() => navigate('/messages')}
          style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          ← Back to messages
        </button>
      </div>
    );
  }

  return (
    <div className="message-thread-page" style={{
      maxWidth: 680, margin: '0 auto', padding: '24px 24px 0',
      display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)',
      boxSizing: 'border-box', width: '100%',
    }}>
      <Helmet>
        <title>{thread ? `${thread.subject} — Messages — Algovex` : 'Messages — Algovex'}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="message-thread-header" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexShrink: 0 }}>
        <button
          onClick={() => navigate('/messages')}
          title="Back to messages"
          style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 6,
            width: 32, height: 32, cursor: 'pointer', color: 'var(--text)', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          {loading ? 'Loading…' : thread?.subject}
        </h1>
      </div>

      <div
        ref={listRef}
        className="message-thread-list"
        style={{
          flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 10,
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10,
          padding: 16, marginBottom: 12, boxSizing: 'border-box',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, padding: 20 }}>Loading…</div>
        ) : (thread?.messages || []).map((m) => (
          <div
            key={m.id}
            style={{
              alignSelf: m.from_staff ? 'flex-start' : 'flex-end',
              maxWidth: '80%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: m.from_staff ? 'flex-start' : 'flex-end',
            }}
          >
            <div style={{
              background: m.from_staff ? 'var(--surface)' : 'var(--teal)',
              color: m.from_staff ? 'var(--text)' : '#fff',
              border: m.from_staff ? '1px solid var(--border)' : 'none',
              borderRadius: 12,
              padding: '9px 13px',
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {m.body}
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3, opacity: 0.7 }}>
              {m.from_staff ? 'Algovex' : 'You'} · {formatTime(m.created_at)}
            </div>
          </div>
        ))}
      </div>

      <div className="message-thread-input-bar" style={{
        flexShrink: 0, padding: '0 0 16px', display: 'flex', gap: 8,
      }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a reply…"
          rows={1}
          disabled={loading}
          style={{
            flex: 1, minWidth: 0, resize: 'none', padding: '10px 14px', borderRadius: 8,
            border: '1px solid var(--border)', background: 'var(--surface)',
            color: 'var(--text)', fontSize: 14, fontFamily: 'inherit', outline: 'none',
            minHeight: 42, maxHeight: 120, overflowY: 'auto', boxSizing: 'border-box',
          }}
          onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
        />
        <button
          onClick={handleSend}
          disabled={loading || sending || !input.trim()}
          style={{
            background: 'var(--teal)', color: '#fff', border: 'none', padding: '0 20px',
            borderRadius: 8, fontSize: 14, fontWeight: 700, flexShrink: 0,
            cursor: (loading || sending || !input.trim()) ? 'default' : 'pointer',
            opacity: (loading || sending || !input.trim()) ? 0.5 : 1,
          }}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .message-thread-page {
            padding: 12px 12px 0 !important;
          }
          .message-thread-header {
            margin-bottom: 10px !important;
          }
          .message-thread-list {
            padding: 12px !important;
          }
          .message-thread-input-bar {
            padding: 0 0 12px !important;
          }
        }
      `}</style>
    </div>
  );
}
